import {useEffect, useMemo, useState} from "react";
import type {FloorplanReservationItemDTO} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";

interface Options {
    enabled: boolean;
    reservations: FloorplanReservationItemDTO[];
}

/**
 * Drives lightweight UI refreshes for time-sensitive floorplan visuals.
 *
 * Purpose:
 * - Keep "now"-dependent rendering current (seat fill, current reservation card, lines)
 * - Refresh exactly when needed (next minute or next reservation edge)
 * - Avoid network calls; this hook only causes local re-render via `tick`
 */
export function useFloorplanTemporalRefresh({enabled, reservations}: Options): number {
    const [tick, setTick] = useState(0);

    const nextEdgeTs = useMemo(() => {
        if (!enabled || !reservations.length) return null;
        const nowTs = Date.now();

        let next: number | null = null;
        for (const item of reservations) {
            const startRaw = item.restaurantReservation?.expectedStartDate;
            const endRaw = item.restaurantReservation?.expectedEndDate;
            const startTs = startRaw ? new Date(startRaw).getTime() : NaN;
            const endTs = endRaw ? new Date(endRaw).getTime() : NaN;

            if (Number.isFinite(startTs) && startTs > nowTs && (next == null || startTs < next)) {
                next = startTs;
            }
            if (Number.isFinite(endTs) && endTs > nowTs && (next == null || endTs < next)) {
                next = endTs;
            }
        }

        return next;
    }, [enabled, reservations]);

    useEffect(() => {
        if (!enabled) return;

        const now = new Date();
        const nextMinute = new Date(now);
        nextMinute.setSeconds(0, 0);
        nextMinute.setMinutes(nextMinute.getMinutes() + 1);
        const nextMinuteTs = nextMinute.getTime();

        const targetTs = nextEdgeTs != null
            ? Math.min(nextMinuteTs, nextEdgeTs)
            : nextMinuteTs;

        const waitMs = Math.max(250, targetTs - Date.now() + 50);
        const id = window.setTimeout(() => {
            setTick(prev => prev + 1);
        }, waitMs);

        return () => window.clearTimeout(id);
    }, [enabled, nextEdgeTs, tick]);

    return tick;
}

